import React, { useCallback, useMemo } from 'react';
import map from 'lodash/map';

import {
  GroupedMultiSelectOptionalProps,
  GroupedMultiSelectValueType
} from './GroupedMultiSelectFieldControl.types';
import {
  MultiSelect,
  MultiSelectProps,
  MultiSelectValueType
} from '../../../../MultiSelect';
import { MultiSelectChangeCallbackType } from '../../../../MultiSelect/types';
import flatten from 'lodash/flatten';
import find from 'lodash/find';

interface GroupedMultiSelectFieldControlRequiredProps {
  selectedValue?: MultiSelectValueType;
  value?: GroupedMultiSelectValueType;
  onChange?: (updateValue: GroupedMultiSelectValueType) => void;
}

function GroupedMultiSelectFieldControl({
  autoFocus,
  classNamePrefix = null,
  closeMenuOnSelect = true,
  data = [],
  defaultValue = null,
  disabled = false,
  emptyValue = null,
  error,
  errorClassName,
  formatGroupLabel,
  formatOptionLabel,
  i18nLabel = null,
  i18nPlaceholder,
  inputWrapperClassName = null,
  isClearable = false,
  isLoading = false,
  isSearchable = false,
  labelClassName,
  menuIsOpen,
  menuPlacement = 'auto',
  menuPosition,
  multi = false,
  name,
  onBlur,
  onChange,
  onInputChange,
  onMenuClose,
  onMenuOpen,
  onMenuScrollToBottom,
  openMenuOnFocus,
  optionsLoading = false,
  placeholder = null,
  showError,
  selectedValue,
  value,
  sortable,
  filterConfig
}: MultiSelectProps &
  GroupedMultiSelectOptionalProps &
  GroupedMultiSelectFieldControlRequiredProps) {
  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (updateValue) => {
      onChange(
        Array.isArray(updateValue)
          ? map(updateValue, 'value')
          : updateValue?.value
      );
    },
    [onChange]
  );

  const multiSelectValue = useMemo<MultiSelectValueType>(() => {
    if (selectedValue !== undefined) {
      return selectedValue;
    }

    const flatData = flatten(map(data, (item) => item.options));

    return Array.isArray(value)
      ? map(value, (value) => find(flatData, (item) => item.value === value))
      : find(flatData, (item) => item.value === value);
  }, [data, selectedValue, value]);

  return (
    <MultiSelect
      autoFocus={autoFocus}
      classNamePrefix={classNamePrefix}
      closeMenuOnSelect={closeMenuOnSelect}
      data={data}
      defaultValue={defaultValue}
      disabled={disabled}
      emptyValue={emptyValue}
      error={error}
      formatGroupLabel={formatGroupLabel}
      formatOptionLabel={formatOptionLabel}
      i18nLabel={i18nLabel}
      i18nPlaceholder={i18nPlaceholder}
      inputWrapperClassName={inputWrapperClassName}
      isClearable={isClearable}
      isSearchable={isSearchable}
      labelClassName={labelClassName}
      menuIsOpen={menuIsOpen}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      multi={multi}
      onChange={handleChange}
      onInputChange={onInputChange}
      onMenuClose={onMenuClose}
      onMenuOpen={onMenuOpen}
      openMenuOnFocus={openMenuOnFocus}
      optionsLoading={optionsLoading}
      placeholder={placeholder}
      value={multiSelectValue}
      name={name}
      onMenuScrollToBottom={onMenuScrollToBottom}
      isLoading={isLoading}
      onBlur={onBlur}
      errorClassName={errorClassName}
      showError={showError}
      sortable={sortable}
      filterConfig={filterConfig}
    />
  );
}

export default GroupedMultiSelectFieldControl;
